<script setup lang="ts">
  import { ref } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Task } from '@stuut/api/models';
  import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
  } from '@ui/components/context-menu';

  import TaskTrigger from '@/components/common/TaskTrigger.vue';
  import TaskDismissAlert from '@/components/tasks/TaskDismissAlert.vue';
  import { useLauncherStore, useTaskCollectionStore } from '@/store';

  interface Props {
    showAssignee?: boolean;
    showClassifications?: boolean;
    showCustomer?: boolean;
    showPriority?: boolean;
    size?: '2xs' | 'xs' | 'sm';
    task: Task;
    textLabelClass?: HTMLAttributes['class'];
    truncate?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    showAssignee: true,
    showClassifications: true,
    showCustomer: true,
    showPriority: true,
    size: 'xs',
  });

  const emits = defineEmits<{
    (e: 'beforeTaskOpen'): void;
  }>();

  const showDismissAlert = ref<boolean>(false);
  const dismissAlertActionType = ref<string | undefined>();

  const launcherStore = useLauncherStore();
  const taskCollectionStore = useTaskCollectionStore();

  const openDismissAlert = (actionType?: string) => {
    showDismissAlert.value = true;
    dismissAlertActionType.value = actionType;
  };

  const contextItemClass =
    'flex items-center h-[35px] px-[10px] relative pl-[20px] select-none outline-hidden leading-none';

  defineOptions({
    inheritAttrs: false,
  });
</script>

<template>
  <ContextMenu>
    <ContextMenuTrigger as-child>
      <TaskTrigger
        v-bind="$attrs"
        :task="task"
        :text-label-class="textLabelClass"
        :size="size"
        :show-customer="showCustomer"
        :show-priority="showPriority"
        :show-assignee="showAssignee"
        :show-classifications="showClassifications"
        :truncate="truncate"
      >
        <template #task-trigger-right>
          <slot name="task-item-content-right" />
        </template>
      </TaskTrigger>
    </ContextMenuTrigger>

    <ContextMenuContent>
      <ContextMenuItem
        :class="contextItemClass"
        @click="
          () => {
            launcherStore.closeChat();
            emits('beforeTaskOpen');
            taskCollectionStore.openTaskDrawer({ task });
          }
        "
      >
        <i class="fa-solid fa-expand text-foreground mr-2.5"></i>
        View task
      </ContextMenuItem>

      <ContextMenuItem :class="contextItemClass" @click="openDismissAlert('close')">
        <i class="fa-regular fa-circle-xmark mr-2 text-destructive"></i>
        Dismiss
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem :class="contextItemClass" @click="openDismissAlert('mark_inaccurate')">
        <i class="fa-regular fa-flag mr-2 text-agent"></i>
        This task isn't accurate
      </ContextMenuItem>
    </ContextMenuContent>
  </ContextMenu>

  <TaskDismissAlert
    v-if="task"
    :task="task"
    :show="showDismissAlert"
    :action-type="dismissAlertActionType"
  />
</template>

<style scoped></style>
